import { Fade, Paper, Box, Typography, Grid, IconButton, alpha, Button, Divider, List, ListItem, ListItemText, Chip, CircularProgress, Grow, TextField, Link, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react'
import { useConfig } from '../Utils/ConfigContext'

import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import AddLocationIcon from '@mui/icons-material/AddLocation'
import RoomIcon from '@mui/icons-material/Room'
import MenuIcon from '@mui/icons-material/Menu'
import InfoIcon from '@mui/icons-material/Info'

export const PopupModal = forwardRef(({ onCoinSelected, refreshGlobeData, onCoinLocationFound, onClose }, ref) => {
    // Add useImperativeHandle to expose methods to parent
    const theme = useTheme()
    const config = useConfig()
    const [showCoinInfo, setShowCoinInfo] = useState(false)
    const [showLocationForm, setShowLocationForm] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [showSuccessAlert, setShowSuccessAlert] = useState(false)
    const [currentRsid, setCurrentRsid] = useState(null)
    const [alertOpen, setAlertOpen] = useState(false)
    const [alertMessage, setAlertMessage] = useState('')
    const [alertSeverity, setAlertSeverity] = useState('error')

    const [locationData, setLocationData] = useState({
        street: '',
        city: '',
        country: '',
        email: '',
        question1: '',
        question2: '',
        question3: ''
    })

    const [coinInfo, setCoinInfo] = useState({
        name: 'Coin Name',
        details: [],
        pastLocations: [],
        isError: false,
        errorMessage: ''
    })

    useImperativeHandle(ref, () => ({
        searchCoin,
        searchCoinById,
        openModal: () => setShowCoinInfo(true),
        closeModal: handleClose
    }))

    useEffect(() => {
        if (showCoinInfo) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = 'auto'
        }

        return () => {
            document.body.style.overflow = 'auto'
        }
    }, [showCoinInfo])
    const handleClose = () => {
        onClose();

        // setCoinInfo({
        //     name: 'Coin Name',
        //     details: [],
        //     pastLocations: [],
        //     isError: false,
        //     errorMessage: ''
        // })
        // setShowLocationForm(false)
        setShowCoinInfo(false)
    }
    const handleAddNewLocation = () => {
        setShowLocationForm(true)
    }

    const handleLocationInputChange = (e) => {
        const { name, value } = e.target
        setLocationData(prev => ({
            ...prev,
            [name]: value
        }))
    }


    const handleLocationSubmit = async (e) => {
        e.preventDefault()

        if (!locationData.city || !locationData.country) {
            setAlertMessage('City and Country are required fields')
            setAlertSeverity('error')
            setAlertOpen(true)
            return
        }

        setIsSubmitting(true)

        try {
 
            const result = await config.addCoinLocation({
                rsid: currentRsid,
                street: locationData.street,
                city: locationData.city,
                country: locationData.country,
                email: locationData.email,
                appreciationMoment: locationData.question1,
                worldNeeds: locationData.question2,
                appreciatedFor: locationData.question3
            })

            if (result.success) {
                // Reset form
                setShowLocationForm(false)
                setLocationData({
                    street: '',
                    city: '',
                    country: '',
                    email: '',
                    question1: '',
                    question2: '',
                    question3: ''
                })

                // Refresh globe data first
                if (refreshGlobeData) {
                    await refreshGlobeData()
                }

                // Get the updated coin history data for the Journey list
                if (currentRsid) {
                    try {
                        // Fetch updated coin tracks to get the newly added location
                        const coinTracksResult = await config.getCoinTracks(currentRsid)

                        if (coinTracksResult.success && coinTracksResult.data && coinTracksResult.data.length > 0) {
                            displayCoinInfo(coinTracksResult, coinInfo.name)

                            // Update globe visualization
                            if (onCoinSelected) {
                                onCoinSelected(currentRsid)
                            }

                            // If we have geo coordinates, highlight the new location
                            const latestLocation = coinTracksResult.data[0]
                            if (latestLocation.geo_lat && latestLocation.geo_lng) {
                                if (onCoinLocationFound) {
                                    onCoinLocationFound(latestLocation.geo_lat, latestLocation.geo_lng)
                                }
                            }
                        }
                    } catch (error) {
                        console.error('Error refreshing coin history after adding location:', error)
                    }
                }

                // Show success alert after data is refreshed
                setShowSuccessAlert(true)
                setAlertMessage('Location added successfully')
                setAlertSeverity('success')
                setAlertOpen(true)

            } else if (result.cooldown) {
                // Handle cooldown response
                setAlertMessage(`${result.message}`)
                setAlertSeverity('error')
                setAlertOpen(true)
            } else {
                setAlertMessage('Failed to add new location: ' + (result.message || 'Unknown error'))
                setAlertSeverity('error')
                setAlertOpen(true)
            }
        } catch (error) {
            if (error.response && error.response.status === 429) {
                const cooldownData = error.response.data
                setAlertMessage(`Please wait before logging another location: ${cooldownData?.message || 'Cooldown period in effect'}`)
                setAlertSeverity('error')
                setAlertOpen(true)
            } else {
                console.error('Error adding new location:', error)
                setAlertMessage('Failed to add new location: ' + (error.message || 'Unknown error'))
                setAlertSeverity('error')
                setAlertOpen(true)
            }
        } finally {
            setIsSubmitting(false);
        }
    };
    const displayCoinInfo = (coinData, name) => {

        if (!coinData.data || coinData.data.length === 0) {
            setCoinInfo(prev => ({
                ...prev,
                name: name, // Always use the provided name (Coin XXXX) instead of code
                details: [{ key: 'info', value: 'No data available' }],
                pastLocations: []
            }))
            return
        }


        const hasTracks = coinData.data && coinData.data.length > 0


        const data = coinData.data[coinData.data.length - 1]
        const details = []
        const pastLocations = []



        if (data.id) {
            details.push({ key: 'id', label: 'ID', value: data.id })
        }

        if (data.value) {
            details.push({ key: 'value', label: 'Value', value: data.value })
        }

        if (data.issued) {
            details.push({ key: 'issued', label: 'Issued', value: data.issued })
        }

        if (data.location) {
            details.push({ key: 'location', label: 'Location', value: data.location })
        }

        if (data.geo_lat && data.geo_lng) {
            details.push({ key: 'coordinates', label: 'Coordinates', value: `${parseFloat(data.geo_lat).toFixed(4)}, ${parseFloat(data.geo_lng).toFixed(4)}` })
        } else if (data.latitude && data.longitude) {
            details.push({ key: 'coordinates', label: 'Coordinates', value: `${parseFloat(data.latitude).toFixed(4)}, ${parseFloat(data.longitude).toFixed(4)}` })
        }

        if (data.batch) {
            details.push({ key: 'batch', label: 'Batch', value: data.batch })
        }

        if (hasTracks) {
            coinData.data.forEach((track, index) => {
                if (track.location) {
                    const date = track.timestamp ? new Date(track.timestamp * 1000).toLocaleDateString() : 'Unknown date'
                    pastLocations.push({
                        key: `location-${index}`,
                        location: track.location,
                        date: date,
                        coordinates: track.geo_lat && track.geo_lng ?
                            `${parseFloat(track.geo_lat).toFixed(4)}, ${parseFloat(track.geo_lng).toFixed(4)}` : null
                    })
                }
            })
        }

        setCoinInfo(prev => ({
            ...prev,
            name: name, // Always use the provided name (Coin XXXX) instead of code
            details,
            pastLocations
        }))
    }

    const [showAddLocation, setShowAddLocation] = useState(false)


    const searchCoinById = async (coinId) => {
        if (!coinId) return

        setShowCoinInfo(true)
        setCoinInfo({
            name: 'Loading...',
            details: [],
            pastLocations: [],
            isError: false,
            errorMessage: ''
        })

        try {
            const coinDataResult = await config.getCoinById(coinId)

            if (coinDataResult.success && coinDataResult.data && coinDataResult.data.length > 0) {
                const rsid = coinDataResult.data[0].rsid
                const code = coinDataResult.data[0].code
                
                // Format the display name as "Coin XXXX" instead of showing the code
                const formattedId = coinId.toString().padStart(4, '0')
                const displayName = `Coin ${formattedId}`
                setShowAddLocation(false)
                setCurrentRsid(rsid)
                displayCoinInfo(coinDataResult, displayName)

                const coinTracksResult = await config.getCoinTracks(rsid)

                if (coinTracksResult.success && coinTracksResult.data && coinTracksResult.data.length > 0) {
                    displayCoinInfo(coinTracksResult, displayName)

                    if (onCoinSelected) {
                        onCoinSelected(rsid)
                    }

                    const lastLocation = coinTracksResult.data[coinTracksResult.data.length - 1]
                    if (lastLocation && lastLocation.geo_lat && lastLocation.geo_lng) {
                        const highlightData = {
                            longitude: lastLocation.geo_lng,
                            latitude: lastLocation.geo_lat
                        }

                        if (onCoinLocationFound) {
                            onCoinLocationFound(highlightData.latitude, highlightData.longitude)
                        }
                    }
                }
            } else {
                setCoinInfo({
                    name: 'Error',
                    details: [{ key: 'error', value: 'Coin not found' }],
                    pastLocations: [],
                    isError: true,
                    errorMessage: 'Coin not found'
                })
            }
        } catch (error) {
            console.error('Error searching coin by ID:', error)
            setCoinInfo({
                name: 'Error',
                details: [{ key: 'error', value: error.message || 'Failed to fetch coin data' }],
                pastLocations: [],
                isError: true,
                errorMessage: error.message || 'Failed to fetch coin data'
            })
        }
    }

    const searchCoin = async (coinId) => {
        if (!coinId) return

        setShowCoinInfo(true)
        setCoinInfo({
            name: 'Loading...',
            details: [],
            pastLocations: [],
            isError: false,
            errorMessage: ''
        })

        try {
            const coinDataResult = await config.getCoinByCode(coinId)

            if (coinDataResult.success && coinDataResult.data && coinDataResult.data.length > 0) {
                const rsid = coinDataResult.data[0].rsid
                const numericId = coinDataResult.data[0].id || coinDataResult.data[0].coinid || ''
                const code = coinDataResult.data[0].code || coinDataResult.data[0].coinid || ''
                // Format the display name as "Coin XXXX" instead of showing the code
                const formattedId = numericId.toString().padStart(4, '0')
                const displayName = code
                setShowAddLocation(true)
                setCurrentRsid(rsid)
                displayCoinInfo(coinDataResult, displayName)

                const coinTracksResult = await config.getCoinTracks(rsid)

                if (coinTracksResult.success && coinTracksResult.data && coinTracksResult.data.length > 0) {
                    displayCoinInfo(coinTracksResult, displayName)

                    if (onCoinSelected) {
                        onCoinSelected(rsid)
                    }

                    const lastLocation = coinTracksResult.data[coinTracksResult.data.length - 1]
                    if (lastLocation && lastLocation.geo_lat && lastLocation.geo_lng) {
                        const highlightData = {
                            longitude: lastLocation.geo_lng,
                            latitude: lastLocation.geo_lat
                        }

                        if (onCoinLocationFound) {
                            onCoinLocationFound(highlightData.latitude, highlightData.longitude)
                        }
                    }
                }
            } else {
                setCoinInfo({
                    name: 'Error',
                    details: [{ key: 'error', value: 'Coin not found' }],
                    pastLocations: [],
                    isError: true,
                    errorMessage: 'Coin not found'
                })
            }
        } catch (error) {
            console.error('Error searching coin:', error)
            setCoinInfo({
                name: 'Error',
                details: [{ key: 'error', value: error.message || 'Failed to fetch coin data' }],
                pastLocations: [],
                isError: true,
                errorMessage: error.message || 'Failed to fetch coin data'
            })
        }
    }
    return (
        <>
            <Fade in={showCoinInfo} unmountOnExit>
                <Paper
                    elevation={24}
                    sx={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        borderRadius: 1,
                        zIndex: 2000,
                        overflow: 'hidden',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.03) 1px, transparent 1px)',
                        backgroundSize: '15px 15px',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.3)}`,
                        display: 'flex',
                        flexDirection: 'column',
                        width: { xs: 'calc(100% - 32px)', sm: '500px', md: '600px', lg: '700px' },
                        maxHeight: { xs: 'calc(100vh - 48px)', sm: 'calc(100vh - 64px)' },
                        maxWidth: 'calc(100% - 32px)',
                        overflowY: 'auto',
                        px: { xs: 0, sm: 0 },
                        py: 0,
                        '&:focus-visible': {
                            outline: 'none'
                        },
                        pointerEvents: 'all',
                        animationName: 'lightboxAppear',
                        animationDuration: '0.3s',
                        animationTimingFunction: 'ease-out'
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            zIndex: 20,
                            p: 2
                        }}
                    >
                        <IconButton
                            onClick={handleClose}
                            size="small"
                            sx={{
                                color: 'white',
                                backdropFilter: 'blur(5px)',
                                borderRadius: '50%',
                                border: '1px solid rgba(255, 255, 255, 0.9)',
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
                                padding: '6px',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)'
                                }
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box
                        sx={{
                            p: 3,
                            pt: 7,
                            pb: 4,
                            mt: 3
                        }}
                    >
                        <Box
                            sx={{
                                borderRadius: 1,
                                overflow: 'hidden',
                                background: 'linear-gradient(135deg, #f5756c 0%, #f76863 50%, #f9b949 100%)',
                                textAlign: 'center',
                                p: 1,
                                mb: 3
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'white',
                                    fontWeight: 'bold',
                                    textShadow: '0px 1px 2px rgba(0,0,0,0.3)',
                                }}
                            >
                                {coinInfo.name}
                            </Typography>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            {(
                                coinInfo.details.length > 0 ? (
                                    <Box>
                                        {!coinInfo.isError && <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'rgba(255, 255, 255, 0.8)',
                                                mb: 2,
                                                textAlign: 'center',
                                                fontSize: '0.85rem'
                                            }}
                                        >
                                            COIN DETAILS
                                        </Typography>}
                                        <Grid container spacing={2}>
                                            {coinInfo.details.map(detail => (
                                                <Grid item xs={12} sm={6} key={detail.key}>
                                                    <Box sx={{
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                        borderRadius: 1,
                                                        p: 1.5,
                                                        height: '100%'
                                                    }}>
                                                        {detail.label && (
                                                            <Typography
                                                                component="span"
                                                                variant="subtitle2"
                                                                sx={{
                                                                    display: 'block',
                                                                    mb: 0.5,
                                                                    fontWeight: 600,
                                                                    color: 'rgba(255, 255, 255, 0.6)',
                                                                    fontSize: '0.7rem',
                                                                    letterSpacing: '0.5px'
                                                                }}
                                                            >
                                                                {detail.label.toUpperCase()}
                                                            </Typography>
                                                        )}
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                color: 'white',
                                                                fontWeight: 500
                                                            }}
                                                        >
                                                            {detail.value}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>

                                        {coinInfo.pastLocations.length > 0 && (
                                            <Box sx={{ mt: 4 }}>
                                                <Box
                                                    sx={{
                                                        borderRadius: 1,
                                                        overflow: 'hidden',
                                                        background: 'linear-gradient(135deg, #f5756c 0%, #f76863 50%, #f9b949 100%)',
                                                        textAlign: 'center',
                                                        p: 1.5,
                                                        mb: 2,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: '100%',
                                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)'
                                                    }}
                                                >
                                                    <RoomIcon sx={{ mr: 1.5, color: 'white' }} />
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            color: 'white',
                                                            fontWeight: 'bold',
                                                            textShadow: '0px 1px 2px rgba(0,0,0,0.3)',
                                                            fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                                            letterSpacing: '0.5px'
                                                        }}
                                                    >
                                                        JOURNEY HISTORY
                                                    </Typography>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        borderRadius: 1,
                                                        p: 0,
                                                        backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                                        border: '1px solid rgba(255, 255, 255, 0.1)'
                                                    }}
                                                >
                                                    <List disablePadding>
                                                        {coinInfo.pastLocations.map((loc, index) => (
                                                            <React.Fragment key={loc.key}>
                                                                {index > 0 && <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />}
                                                                <ListItem
                                                                    sx={{
                                                                        py: 1.5,
                                                                        px: 2,
                                                                        '&:hover': {
                                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)'
                                                                        }
                                                                    }}
                                                                >
                                                                    <Chip
                                                                        label={index + 1}
                                                                        size="small"
                                                                        sx={{
                                                                            mr: 2,
                                                                            borderRadius: '50%',
                                                                            minWidth: 30,
                                                                            height: 30,
                                                                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                                            color: 'white',
                                                                            fontWeight: 'bold',
                                                                            border: '1px solid rgba(255, 255, 255, 0.2)'
                                                                        }}
                                                                    />
                                                                    <ListItemText
                                                                        primary={
                                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                                                                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                                                    <Typography
                                                                                        component="span"
                                                                                        variant="subtitle2"
                                                                                        sx={{
                                                                                            fontWeight: 500,
                                                                                            fontSize: '0.7rem',
                                                                                            letterSpacing: '0.5px',
                                                                                            color: 'rgba(255, 255, 255, 0.6)',
                                                                                            textTransform: 'uppercase'
                                                                                        }}
                                                                                    >
                                                                                        Location
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        sx={{
                                                                                            color: 'white',
                                                                                            fontWeight: 500
                                                                                        }}
                                                                                    >
                                                                                        {loc.location}
                                                                                    </Typography>
                                                                                </Box>

                                                                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                                                    <Typography
                                                                                        component="span"
                                                                                        variant="subtitle2"
                                                                                        sx={{
                                                                                            fontWeight: 500,
                                                                                            fontSize: '0.7rem',
                                                                                            letterSpacing: '0.5px',
                                                                                            color: 'rgba(255, 255, 255, 0.6)',
                                                                                            textTransform: 'uppercase'
                                                                                        }}
                                                                                    >
                                                                                        Date
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        sx={{
                                                                                            color: 'white',
                                                                                            fontWeight: 500
                                                                                        }}
                                                                                    >
                                                                                        {loc.date}
                                                                                    </Typography>
                                                                                </Box>

                                                                                {loc.coordinates && (
                                                                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                                                        <Typography
                                                                                            component="span"
                                                                                            variant="subtitle2"
                                                                                            sx={{
                                                                                                fontWeight: 500,
                                                                                                fontSize: '0.7rem',
                                                                                                letterSpacing: '0.5px',
                                                                                                color: 'rgba(255, 255, 255, 0.8)',
                                                                                                textTransform: 'uppercase'
                                                                                            }}
                                                                                        >
                                                                                            Coordinates
                                                                                        </Typography>
                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            sx={{
                                                                                                color: 'white',
                                                                                                textShadow: '0 0 2px rgba(255, 255, 255, 0.3)',
                                                                                                fontWeight: 500
                                                                                            }}
                                                                                        >
                                                                                            {loc.coordinates}
                                                                                        </Typography>
                                                                                    </Box>
                                                                                )}
                                                                            </Box>
                                                                        }
                                                                    />
                                                                </ListItem>
                                                            </React.Fragment>
                                                        ))}
                                                    </List>
                                                </Box>
                                            </Box>
                                        )}

                                        {!showLocationForm && currentRsid && showAddLocation && (
                                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleAddNewLocation}
                                                    startIcon={<AddLocationIcon />}
                                                    sx={{
                                                        borderRadius: 1,
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        px: 3,
                                                        py: 1,
                                                        background: 'linear-gradient(135deg, #f5756c 0%, #f76863 50%, #f9b949 100%)',
                                                        color: 'white',
                                                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.25)',
                                                        '&:hover': {
                                                            background: 'linear-gradient(135deg, #f5756c 0%, #f76863 60%, #f9b949 100%)',
                                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                                                        }
                                                    }}
                                                >
                                                    ADD NEW LOCATION
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                ) : (
                                    <Typography variant="body1" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                        No details available
                                    </Typography>
                                )
                            )}
                        </Box>

                        {showLocationForm && (
                            <Grow in={showLocationForm} timeout={300}>
                                <Box sx={{
                                    mt: 4,
                                    pb: { xs: 4, sm: 2 }
                                }}>
                                    <Box
                                        sx={{
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            background: 'linear-gradient(135deg, #f5756c 0%, #f76863 50%, #f9b949 100%)',
                                            textAlign: 'center',
                                            p: 1.5,
                                            mb: 3,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '100%',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)'
                                        }}
                                    >
                                        <AddLocationIcon sx={{ mr: 1.5, color: 'white' }} />
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: 'white',
                                                fontWeight: 'bold',
                                                textShadow: '0px 1px 2px rgba(0,0,0,0.3)',
                                                fontSize: { xs: '0.95rem', sm: '1.1rem' },
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            LOG A NEW LOCATION
                                        </Typography>
                                    </Box>

                                    <form onSubmit={handleLocationSubmit}>
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: { xs: 3, sm: 2 }
                                        }}>
                                            {/* Mobile list view */}
                                            <Box
                                                sx={{
                                                    display: { xs: 'flex', sm: 'none' },
                                                    flexDirection: 'column',
                                                    gap: 3
                                                }}
                                            >
                                                <Box>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            mb: 1.5,
                                                            color: 'rgba(255, 255, 255, 0.7)',
                                                            fontWeight: 500,
                                                            textTransform: 'uppercase',
                                                            fontSize: '0.75rem',
                                                            letterSpacing: '0.5px'
                                                        }}
                                                    >
                                                        Location Details
                                                    </Typography>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            mb: 1,
                                                            color: 'rgba(255, 255, 255, 0.9)',
                                                            fontWeight: 500,
                                                            fontSize: '0.85rem',
                                                            letterSpacing: '0.3px'
                                                        }}
                                                    >
                                                        Street (optional)
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        name="street"
                                                        value={locationData.street}
                                                        onChange={handleLocationInputChange}
                                                        variant="outlined"
                                                        size="small"
                                                        placeholder="Enter street address"
                                                        sx={{
                                                            mb: 2,
                                                            '.MuiOutlinedInput-root': {
                                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                                borderRadius: 1,
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)'
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(255, 255, 255, 0.6)'
                                                                },
                                                                '&.Mui-focused': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                                    boxShadow: '0 0 0 2px rgba(255, 155, 80, 0.3)'
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(249, 185, 73, 0.8)',
                                                                    borderWidth: '1px'
                                                                }
                                                            },
                                                            '.MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                                                borderWidth: '1px'
                                                            },
                                                            '.MuiInputBase-input': {
                                                                color: 'white',
                                                                fontSize: '0.9rem',
                                                                fontWeight: 400,
                                                                '&::placeholder': {
                                                                    color: 'rgba(255, 255, 255, 0.6)',
                                                                    opacity: 1
                                                                }
                                                            }
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            mb: 1,
                                                            color: 'rgba(255, 255, 255, 0.9)',
                                                            fontWeight: 500,
                                                            fontSize: '0.85rem',
                                                            letterSpacing: '0.3px'
                                                        }}
                                                    >
                                                        City (required) *
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        name="city"
                                                        value={locationData.city}
                                                        onChange={handleLocationInputChange}
                                                        variant="outlined"
                                                        required
                                                        size="small"
                                                        placeholder="Enter city name"
                                                        sx={{
                                                            mb: 2,
                                                            '.MuiOutlinedInput-root': {
                                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                                borderRadius: 1,
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)'
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(255, 255, 255, 0.6)'
                                                                },
                                                                '&.Mui-focused': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                                    boxShadow: '0 0 0 2px rgba(255, 155, 80, 0.3)'
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(249, 185, 73, 0.8)',
                                                                    borderWidth: '1px'
                                                                }
                                                            },
                                                            '.MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                                                borderWidth: '1px'
                                                            },
                                                            '.MuiInputBase-input': {
                                                                color: 'white',
                                                                fontSize: '0.9rem',
                                                                fontWeight: 400,
                                                                '&::placeholder': {
                                                                    color: 'rgba(255, 255, 255, 0.6)',
                                                                    opacity: 1
                                                                }
                                                            }
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            mb: 1,
                                                            color: 'rgba(255, 255, 255, 0.9)',
                                                            fontWeight: 500,
                                                            fontSize: '0.85rem',
                                                            letterSpacing: '0.3px'
                                                        }}
                                                    >
                                                        Country (required) *
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        name="country"
                                                        value={locationData.country}
                                                        onChange={handleLocationInputChange}
                                                        variant="outlined"
                                                        required
                                                        size="small"
                                                        placeholder="Enter country name"
                                                        sx={{
                                                            '.MuiOutlinedInput-root': {
                                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                                borderRadius: 1,
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)'
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(255, 255, 255, 0.6)'
                                                                },
                                                                '&.Mui-focused': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                                    boxShadow: '0 0 0 2px rgba(255, 155, 80, 0.3)'
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(249, 185, 73, 0.8)',
                                                                    borderWidth: '1px'
                                                                }
                                                            },
                                                            '.MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                                                borderWidth: '1px'
                                                            },
                                                            '.MuiInputBase-input': {
                                                                color: 'white',
                                                                fontSize: '0.9rem',
                                                                fontWeight: 400,
                                                                '&::placeholder': {
                                                                    color: 'rgba(255, 255, 255, 0.6)',
                                                                    opacity: 1
                                                                }
                                                            }
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            display: { xs: 'none', md: 'block' },
                                                            mb: 1,
                                                            color: 'rgba(255, 255, 255, 0.9)',
                                                            fontWeight: 500,
                                                            fontSize: '0.85rem',
                                                            letterSpacing: '0.3px'
                                                        }}
                                                    >
                                                        Email (optional)
                                                    </Typography>
                                                                                                <TextField
                                                fullWidth
                                                name="email"
                                                value={locationData.email}
                                                onChange={handleLocationInputChange}
                                                variant="outlined"
                                                size="small"
                                                placeholder="Enter email address"
                                                sx={{
                                                    display: { xs: 'none', md: 'block' },
                                                    '.MuiOutlinedInput-root': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                        borderRadius: 1,
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.15)'
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: 'rgba(255, 255, 255, 0.6)'
                                                        },
                                                        '&.Mui-focused': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                            boxShadow: '0 0 0 2px rgba(255, 155, 80, 0.3)'
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: 'rgba(249, 185, 73, 0.8)',
                                                            borderWidth: '1px'
                                                        }
                                                    },
                                                    '.MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                                        borderWidth: '1px'
                                                    },
                                                    '.MuiInputBase-input': {
                                                        color: 'white',
                                                        fontSize: '0.9rem',
                                                        fontWeight: 400,
                                                        '&::placeholder': {
                                                            color: 'rgba(255, 255, 255, 0.6)',
                                                            opacity: 1
                                                        }
                                                    }
                                                }}
                                            />
                                            <Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' }, mb: 1.5, color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                                                Want to support the movement? Reach out to us at{' '}
                                                <Link
                                                    href="mailto:app@reciation.org"
                                                    sx={{
                                                        color: '#f9b949',
                                                        textDecoration: 'underline',
                                                        '&:hover': {
                                                            color: '#f5756c'
                                                        }
                                                    }}
                                                >
                                                    app@reciation.org
                                                </Link>
                                            </Typography>
                                                </Box>

                                                <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', my: 2 }} />

                                                <Box>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            mb: 1.5,
                                                            color: 'rgba(255, 255, 255, 0.7)',
                                                            fontWeight: 500,
                                                            textTransform: 'uppercase',
                                                            fontSize: '0.75rem',
                                                            letterSpacing: '0.5px'
                                                        }}
                                                    >
                                                        Reflection Questions
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mb: 1.5, color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                                                        What do you appreciate at this moment in time?
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        name="question1"
                                                        value={locationData.question1}
                                                        onChange={handleLocationInputChange}
                                                        variant="outlined"
                                                        multiline
                                                        rows={3}
                                                        sx={{
                                                            mb: 3,
                                                            '.MuiOutlinedInput-root': {
                                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                                borderRadius: 1,
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)'
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(255, 255, 255, 0.6)'
                                                                },
                                                                '&.Mui-focused': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                                    boxShadow: '0 0 0 2px rgba(255, 155, 80, 0.3)'
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(249, 185, 73, 0.8)',
                                                                    borderWidth: '1px'
                                                                }
                                                            },
                                                            '.MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                                                borderWidth: '1px'
                                                            },
                                                            '.MuiInputBase-input': {
                                                                color: 'white',
                                                                fontSize: '0.9rem',
                                                                fontWeight: 400,
                                                                '&::placeholder': {
                                                                    color: 'rgba(255, 255, 255, 0.6)',
                                                                    opacity: 1
                                                                }
                                                            }
                                                        }}
                                                        placeholder="Your answer"
                                                    />

                                                    <Typography variant="body2" sx={{ mb: 1.5, color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                                                        What do you think? Our world needs more:
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        name="question2"
                                                        value={locationData.question2}
                                                        onChange={handleLocationInputChange}
                                                        variant="outlined"
                                                        multiline
                                                        rows={3}
                                                        sx={{
                                                            mb: 3,
                                                            '.MuiOutlinedInput-root': {
                                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                                borderRadius: 1,
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)'
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(255, 255, 255, 0.6)'
                                                                },
                                                                '&.Mui-focused': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                                    boxShadow: '0 0 0 2px rgba(255, 155, 80, 0.3)'
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(249, 185, 73, 0.8)',
                                                                    borderWidth: '1px'
                                                                }
                                                            },
                                                            '.MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                                                borderWidth: '1px'
                                                            },
                                                            '.MuiInputBase-input': {
                                                                color: 'white',
                                                                fontSize: '0.9rem',
                                                                fontWeight: 400,
                                                                '&::placeholder': {
                                                                    color: 'rgba(255, 255, 255, 0.6)',
                                                                    opacity: 1
                                                                }
                                                            }
                                                        }}
                                                        placeholder="Your answer"
                                                    />

                                                    <Typography variant="body2" sx={{ mb: 1.5, color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                                                        What is it people might appreciate you for?
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        name="question3"
                                                        value={locationData.question3}
                                                        onChange={handleLocationInputChange}
                                                        variant="outlined"
                                                        multiline
                                                        rows={3}
                                                        sx={{
                                                            '.MuiOutlinedInput-root': {
                                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                                borderRadius: 1,
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)'
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(255, 255, 255, 0.6)'
                                                                },
                                                                '&.Mui-focused': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                                    boxShadow: '0 0 0 2px rgba(255, 155, 80, 0.3)'
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(249, 185, 73, 0.8)',
                                                                    borderWidth: '1px'
                                                                }
                                                            },
                                                            '.MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                                                borderWidth: '1px'
                                                            },
                                                            '.MuiInputBase-input': {
                                                                color: 'white',
                                                                fontSize: '0.9rem',
                                                                fontWeight: 400,
                                                                '&::placeholder': {
                                                                    color: 'rgba(255, 255, 255, 0.6)',
                                                                    opacity: 1
                                                                }
                                                            }
                                                        }}
                                                        placeholder="Your answer"
                                                    />
                                                </Box>
                                            </Box>

                                            {/* Desktop grid view */}
                                            <Grid
                                                container
                                                spacing={3}
                                                sx={{ display: { xs: 'none', sm: 'flex' } }}
                                            >
                                                <Grid item xs={12}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            mb: 1,
                                                            color: 'rgba(255, 255, 255, 0.9)',
                                                            fontWeight: 500,
                                                            fontSize: '0.85rem',
                                                            letterSpacing: '0.3px'
                                                        }}
                                                    >
                                                        Street (optional)
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        name="street"
                                                        value={locationData.street}
                                                        onChange={handleLocationInputChange}
                                                        variant="outlined"
                                                        placeholder="Enter street address"
                                                        sx={{
                                                            mb: 2,
                                                            '.MuiOutlinedInput-root': {
                                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                                borderRadius: 1,
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)'
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(255, 255, 255, 0.6)'
                                                                },
                                                                '&.Mui-focused': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                                    boxShadow: '0 0 0 2px rgba(255, 155, 80, 0.3)'
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(249, 185, 73, 0.8)',
                                                                    borderWidth: '1px'
                                                                }
                                                            },
                                                            '.MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                                                borderWidth: '1px'
                                                            },
                                                            '.MuiInputBase-input': {
                                                                color: 'white',
                                                                fontSize: '0.9rem',
                                                                fontWeight: 400,
                                                                '&::placeholder': {
                                                                    color: 'rgba(255, 255, 255, 0.6)',
                                                                    opacity: 1
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            mb: 1,
                                                            color: 'rgba(255, 255, 255, 0.9)',
                                                            fontWeight: 500,
                                                            fontSize: '0.85rem',
                                                            letterSpacing: '0.3px'
                                                        }}
                                                    >
                                                        City (required) *
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        name="city"
                                                        value={locationData.city}
                                                        onChange={handleLocationInputChange}
                                                        variant="outlined"
                                                        required
                                                        placeholder="Enter city name"
                                                        sx={{
                                                            mb: 2,
                                                            '.MuiOutlinedInput-root': {
                                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                                borderRadius: 1,
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)'
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(255, 255, 255, 0.6)'
                                                                },
                                                                '&.Mui-focused': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                                    boxShadow: '0 0 0 2px rgba(255, 155, 80, 0.3)'
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(249, 185, 73, 0.8)',
                                                                    borderWidth: '1px'
                                                                }
                                                            },
                                                            '.MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                                                borderWidth: '1px'
                                                            },
                                                            '.MuiInputBase-input': {
                                                                color: 'white',
                                                                fontSize: '0.9rem',
                                                                fontWeight: 400,
                                                                '&::placeholder': {
                                                                    color: 'rgba(255, 255, 255, 0.6)',
                                                                    opacity: 1
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12} sm={6}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            mb: 1,
                                                            color: 'rgba(255, 255, 255, 0.9)',
                                                            fontWeight: 500,
                                                            fontSize: '0.85rem',
                                                            letterSpacing: '0.3px'
                                                        }}
                                                    >
                                                        Country (required) *
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        name="country"
                                                        value={locationData.country}
                                                        onChange={handleLocationInputChange}
                                                        variant="outlined"
                                                        required
                                                        placeholder="Enter country name"
                                                        sx={{
                                                            mb: 2,
                                                            '.MuiOutlinedInput-root': {
                                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                                borderRadius: 1,
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)'
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(255, 255, 255, 0.6)'
                                                                },
                                                                '&.Mui-focused': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                                    boxShadow: '0 0 0 2px rgba(255, 155, 80, 0.3)'
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(249, 185, 73, 0.8)',
                                                                    borderWidth: '1px'
                                                                }
                                                            },
                                                            '.MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                                                borderWidth: '1px'
                                                            },
                                                            '.MuiInputBase-input': {
                                                                color: 'white',
                                                                fontSize: '0.9rem',
                                                                fontWeight: 400,
                                                                '&::placeholder': {
                                                                    color: 'rgba(255, 255, 255, 0.6)',
                                                                    opacity: 1
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            mb: 1,
                                                            color: 'rgba(255, 255, 255, 0.9)',
                                                            fontWeight: 500,
                                                            fontSize: '0.85rem',
                                                            letterSpacing: '0.3px'
                                                        }}
                                                    >
                                                        What do you appreciate at this moment in time?
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        name="question1"
                                                        value={locationData.question1}
                                                        onChange={handleLocationInputChange}
                                                        variant="outlined"
                                                        multiline
                                                        rows={3}
                                                        placeholder="Your answer"
                                                        sx={{
                                                            mb: 3,
                                                            '.MuiOutlinedInput-root': {
                                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                                borderRadius: 1,
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)'
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(255, 255, 255, 0.6)'
                                                                },
                                                                '&.Mui-focused': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                                    boxShadow: '0 0 0 2px rgba(255, 155, 80, 0.3)'
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(249, 185, 73, 0.8)',
                                                                    borderWidth: '1px'
                                                                }
                                                            },
                                                            '.MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                                                borderWidth: '1px'
                                                            },
                                                            '.MuiInputBase-input': {
                                                                color: 'white',
                                                                fontSize: '0.9rem',
                                                                fontWeight: 400,
                                                                '&::placeholder': {
                                                                    color: 'rgba(255, 255, 255, 0.6)',
                                                                    opacity: 1
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            mb: 1,
                                                            color: 'rgba(255, 255, 255, 0.9)',
                                                            fontWeight: 500,
                                                            fontSize: '0.85rem',
                                                            letterSpacing: '0.3px'
                                                        }}
                                                    >
                                                        What do you think? Our world needs more:
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        name="question2"
                                                        value={locationData.question2}
                                                        onChange={handleLocationInputChange}
                                                        variant="outlined"
                                                        multiline
                                                        rows={3}
                                                        placeholder="Your answer"
                                                        sx={{
                                                            mb: 3,
                                                            '.MuiOutlinedInput-root': {
                                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                                borderRadius: 1,
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)'
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(255, 255, 255, 0.6)'
                                                                },
                                                                '&.Mui-focused': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                                    boxShadow: '0 0 0 2px rgba(255, 155, 80, 0.3)'
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(249, 185, 73, 0.8)',
                                                                    borderWidth: '1px'
                                                                }
                                                            },
                                                            '.MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                                                borderWidth: '1px'
                                                            },
                                                            '.MuiInputBase-input': {
                                                                color: 'white',
                                                                fontSize: '0.9rem',
                                                                fontWeight: 400,
                                                                '&::placeholder': {
                                                                    color: 'rgba(255, 255, 255, 0.6)',
                                                                    opacity: 1
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            mb: 1,
                                                            color: 'rgba(255, 255, 255, 0.9)',
                                                            fontWeight: 500,
                                                            fontSize: '0.85rem',
                                                            letterSpacing: '0.3px'
                                                        }}
                                                    >
                                                        What is it people might appreciate you for?
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        name="question3"
                                                        value={locationData.question3}
                                                        onChange={handleLocationInputChange}
                                                        variant="outlined"
                                                        multiline
                                                        rows={3}
                                                        placeholder="Your answer"
                                                        sx={{
                                                            mb: 3,
                                                            '.MuiOutlinedInput-root': {
                                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                                borderRadius: 1,
                                                                transition: 'all 0.2s',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)'
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(255, 255, 255, 0.6)'
                                                                },
                                                                '&.Mui-focused': {
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                                    boxShadow: '0 0 0 2px rgba(255, 155, 80, 0.3)'
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'rgba(249, 185, 73, 0.8)',
                                                                    borderWidth: '1px'
                                                                }
                                                            },
                                                            '.MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                                                borderWidth: '1px'
                                                            },
                                                            '.MuiInputBase-input': {
                                                                color: 'white',
                                                                fontSize: '0.9rem',
                                                                fontWeight: 400,
                                                                '&::placeholder': {
                                                                    color: 'rgba(255, 255, 255, 0.6)',
                                                                    opacity: 1
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    mb: 1,
                                                    color: 'rgba(255, 255, 255, 0.9)',
                                                    fontWeight: 500,
                                                    fontSize: '0.85rem',
                                                    letterSpacing: '0.3px'
                                                }}
                                            >
                                                Email (optional)
                                            </Typography>
                                            <TextField
                                                fullWidth
                                                name="email"
                                                value={locationData.email}
                                                onChange={handleLocationInputChange}
                                                variant="outlined"
                                                size="small"
                                                placeholder="Enter email address"
                                                sx={{
                                                    '.MuiOutlinedInput-root': {
                                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                        borderRadius: 1,
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.15)'
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: 'rgba(255, 255, 255, 0.6)'
                                                        },
                                                        '&.Mui-focused': {
                                                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                                                            boxShadow: '0 0 0 2px rgba(255, 155, 80, 0.3)'
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: 'rgba(249, 185, 73, 0.8)',
                                                            borderWidth: '1px'
                                                        }
                                                    },
                                                    '.MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                                        borderWidth: '1px'
                                                    },
                                                    '.MuiInputBase-input': {
                                                        color: 'white',
                                                        fontSize: '0.9rem',
                                                        fontWeight: 400,
                                                        '&::placeholder': {
                                                            color: 'rgba(255, 255, 255, 0.6)',
                                                            opacity: 1
                                                        }
                                                    }
                                                }}
                                            />
                                            <Typography variant="body2" sx={{ mb: 1.5, color: 'rgba(255, 255, 255, 0.8)', fontWeight: 500 }}>
                                                Want to support the movement? Reach out to us at{' '}
                                                <Link
                                                    href="mailto:app@reciation.org"
                                                    sx={{
                                                        color: '#f9b949',
                                                        textDecoration: 'underline',
                                                        '&:hover': {
                                                            color: '#f5756c'
                                                        }
                                                    }}
                                                >
                                                    app@reciation.org
                                                </Link>
                                            </Typography>
                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                                gap: 2,
                                                mt: { xs: 3, sm: 2 },
                                                mb: { xs: 4, sm: 0 }
                                            }}>
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => setShowLocationForm(false)}
                                                    disabled={isSubmitting}
                                                    sx={{
                                                        borderRadius: 1,
                                                        textTransform: 'uppercase',
                                                        px: 3,
                                                        borderColor: 'rgba(255, 255, 255, 0.2)',
                                                        color: 'rgba(255, 255, 255, 0.8)',
                                                        fontWeight: 500,
                                                        letterSpacing: '0.5px',
                                                        fontSize: '0.8rem',
                                                        '&:hover': {
                                                            borderColor: 'rgba(255, 255, 255, 0.4)',
                                                            backgroundColor: 'rgba(255, 255, 255, 0.05)'
                                                        }
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    disabled={isSubmitting}
                                                    sx={{
                                                        borderRadius: 1,
                                                        textTransform: 'uppercase',
                                                        px: 3,
                                                        backgroundColor: isSubmitting ? 'rgba(255, 255, 255, 0.1)' : 'linear-gradient(135deg, #f5756c 0%, #f76863 50%, #f9b949 100%)',
                                                        background: isSubmitting ? 'rgba(255, 255, 255, 0.1)' : 'linear-gradient(135deg, #f5756c 0%, #f76863 50%, #f9b949 100%)',
                                                        color: 'white',
                                                        fontWeight: 500,
                                                        letterSpacing: '0.5px',
                                                        fontSize: '0.8rem',
                                                        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.25)',
                                                        '&:hover': {
                                                            background: 'linear-gradient(135deg, #f5756c 0%, #f76863 60%, #f9b949 100%)',
                                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                                                        }
                                                    }}
                                                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                                                >
                                                    {isSubmitting ? 'ADDING LOCATION...' : 'SUBMIT'}
                                                </Button>

                                            </Box>
                                        </Box>
                                    </form>
                                </Box>
                            </Grow>
                        )}
                    </Box>
                </Paper>
            </Fade>
            <Dialog
                open={alertOpen}
                onClose={() => setAlertOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{
                    sx: {
                        backgroundColor: alpha(theme.palette.background.default, 0.9),
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha(alertSeverity === 'success' ? '#4caf50' : theme.palette.primary.main, 0.2)}`,
                        color: theme.palette.text.primary,
                        borderRadius: 1
                    }
                }}
            >
                <DialogTitle id="alert-dialog-title" sx={{ 
                    pb: 1,
                    color: alertSeverity === 'success' ? '#4caf50' : alertSeverity === 'warning' ? '#ff9800' : '#f44336'
                }}>
                    {alertSeverity === 'success' ? 'Success' : 'Notification'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description" sx={{ color: 'text.primary' }}>
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button 
                        onClick={() => setAlertOpen(false)} 
                        variant="contained"
                        sx={{
                            borderRadius: 1,
                            textTransform: 'uppercase',
                            px: 3,
                            backgroundColor: alertSeverity === 'success' ? 
                                'linear-gradient(135deg, #43a047 0%, #66bb6a 50%, #81c784 100%)' : 
                                'linear-gradient(135deg, #f5756c 0%, #f76863 50%, #f9b949 100%)',
                            background: alertSeverity === 'success' ? 
                                'linear-gradient(135deg, #43a047 0%, #66bb6a 50%, #81c784 100%)' : 
                                'linear-gradient(135deg, #f5756c 0%, #f76863 50%, #f9b949 100%)',
                            color: 'white',
                            fontWeight: 500,
                            letterSpacing: '0.5px',
                            fontSize: '0.8rem',
                            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.25)',
                            '&:hover': {
                                background: alertSeverity === 'success' ?
                                    'linear-gradient(135deg, #43a047 0%, #66bb6a 60%, #81c784 100%)' :
                                    'linear-gradient(135deg, #f5756c 0%, #f76863 60%, #f9b949 100%)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                            }
                        }}
                        autoFocus
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
})